import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { CalendarRange, MapPin } from "lucide-react";

const HOUSE_TONE = {
  "Bonhams": { dot: "bg-amber-400", ring: "ring-amber-400/30", text: "text-amber-400" },
  "Christie's": { dot: "bg-cyan-400", ring: "ring-cyan-400/30", text: "text-cyan-400" },
  "Sotheby's": { dot: "bg-violet-400", ring: "ring-violet-400/30", text: "text-violet-400" },
  "Phillips": { dot: "bg-emerald-400", ring: "ring-emerald-400/30", text: "text-emerald-400" },
};
const STATUS_TONE = {
  "This week": "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30",
  "Upcoming": "bg-amber-500/15 text-amber-400 border border-amber-500/30",
};
const HOUSES = ["Bonhams", "Christie's", "Sotheby's", "Phillips"];

export default function Timeline() {
  const [house, setHouse] = useState("All");
  const { data: schedule = [] } = useQuery({
    queryKey: ["schedule"],
    queryFn: async () => (await api.get("/schedule")).data,
  });

  const upcoming = schedule
    .filter((s) => s.status !== "Past")
    .filter((s) => house === "All" || s.auction_house === house)
    .sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div>
      <p className="text-[11px] font-mono uppercase tracking-widest text-amber-400/90">Across All Four Houses</p>
      <h1 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight flex items-center gap-3">
        <CalendarRange className="w-7 h-7 text-amber-400" /> Upcoming Sales Timeline
      </h1>
      <p className="text-sm text-slate-400 mt-2 max-w-2xl">Every upcoming and current-week sale from Bonhams, Christie's, Sotheby's and Phillips, in one chronological view.</p>

      <div className="flex flex-wrap gap-2 mt-6" data-testid="timeline-house-filter">
        {["All", ...HOUSES].map((h) => (
          <button
            key={h}
            data-testid={`timeline-filter-${h.replace(/[^a-z]/gi, "").toLowerCase()}`}
            onClick={() => setHouse(h)}
            className={`al-chip transition-colors ${house === h ? "bg-amber-500/15 text-amber-400 border border-amber-500/40" : "border border-slate-700 text-slate-400 hover:text-slate-200"}`}
          >
            {h}
          </button>
        ))}
      </div>

      <div className="mt-8 relative" data-testid="timeline-list">
        {upcoming.length === 0 && (
          <p className="text-slate-500 text-sm py-10 text-center al-card">No upcoming sales for this selection.</p>
        )}
        {upcoming.length > 0 && <div className="absolute left-[7px] sm:left-[88px] top-2 bottom-2 w-px bg-slate-800" />}

        <div className="space-y-4">
          {upcoming.map((s) => {
            const tone = HOUSE_TONE[s.auction_house] || HOUSE_TONE["Bonhams"];
            const d = new Date(s.date);
            return (
              <div key={s.schedule_id} className="relative flex items-start gap-4 sm:gap-6" data-testid={`timeline-item-${s.schedule_id}`}>
                <div className="hidden sm:flex flex-col items-end w-[72px] shrink-0 pt-1">
                  <span className="mono-num text-lg text-slate-200 leading-none">{d.getDate()}</span>
                  <span className="text-[11px] font-mono uppercase tracking-widest text-slate-500">{d.toLocaleDateString("en-GB", { month: "short" })}</span>
                  <span className="text-[10px] font-mono text-slate-600">{d.getFullYear()}</span>
                </div>
                <div className={`relative z-10 mt-2 w-3.5 h-3.5 rounded-full ring-4 ${tone.dot} ${tone.ring} shrink-0`} />
                <div className="al-card p-4 flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className={`text-[11px] font-mono uppercase tracking-widest ${tone.text}`}>{s.auction_house}</p>
                      <p className="text-base text-slate-100 truncate mt-0.5">{s.sale_name}</p>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span>{s.auction_type}</span>
                        <span className="text-slate-700">·</span>
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.sale_location}</span>
                        <span className="text-slate-700 sm:hidden">·</span>
                        <span className="mono-num sm:hidden">{fmtDate(s.date)}</span>
                      </p>
                    </div>
                    {STATUS_TONE[s.status] && <span className={`al-chip shrink-0 ${STATUS_TONE[s.status]}`}>{s.status}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
